// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import { engineScopedText, projectFlightDataForEngine } from "../core/engines.js";
import {
  averageValues,
} from "../core/math.js";
import {
  cylinderKeys,
} from "../core/schema.js";

// Consecutive supported samples only. Removed or missing rows must not become
// apparent evidence for the time between disconnected operating segments.
export function continuousAnalysisRuns(data, isUsable = () => true, { maxGapSeconds = 10, maxGapSampleMultiple = 3 } = {}) {
  const gapLimit = Math.min(maxGapSeconds, rollingMedianStep(data) * maxGapSampleMultiple);
  const runs = [];
  let run = [];
  const flush = () => { if (run.length) runs.push(run); run = []; };
  for (const point of data) {
    const time = point?.elapsedSeconds;
    if (!Number.isFinite(time) || !isUsable(point)) { flush(); continue; }
    if (run.length) {
      const step = time - run.at(-1).elapsedSeconds;
      if (!(step > 0) || step > gapLimit) flush();
    }
    run.push(point);
  }
  flush();
  return runs;
}

export function evidenceIntervalSeconds(intervals) {
  const sorted = intervals.filter(interval => Number.isFinite(interval.start) && Number.isFinite(interval.end)
    && interval.end > interval.start).slice().sort((a, b) => a.start - b.start);
  let seconds = 0;
  let end = -Infinity;
  for (const interval of sorted) {
    if (interval.end > end) seconds += interval.end - Math.max(interval.start, end);
    end = Math.max(end, interval.end);
  }
  return seconds;
}

// Aircraft-level confidence must include engines that could not be evaluated.
export function withEngineCoverageConfidence(metric) {
  const entries = metric.details?.engines;
  if (!Array.isArray(entries) || entries.length < 2) return metric;
  const engines = entries.map(entry => ({ engineKey: entry.engineKey, label: entry.label,
    status: entry.details?.coverage?.status ?? "unavailable",
    evaluatedSeconds: entry.details?.coverage?.evaluatedSeconds ?? 0,
    confidence: entry.details?.confidence?.label ?? "Low" }));
  const reasonCodes = engines.filter(engine => engine.status !== "evaluated" || engine.confidence === "Low")
    .map(engine => `limited-engine-coverage:${engine.engineKey}`);
  return { ...metric, details: { ...metric.details,
    coverage: { status: engines.every(engine => engine.status === "evaluated") ? "evaluated"
      : engines.some(engine => engine.status === "evaluated") ? "partial" : "unavailable",
    evaluatedSeconds: Math.min(...engines.map(engine => engine.evaluatedSeconds)), engines },
    confidence: { label: reasonCodes.length ? "Low" : "Medium", reasonCodes } } };
}

export function quantile(values, q) {
  const sorted = values.filter(Number.isFinite).sort((a, b) => a - b);
  if (!sorted.length) return null;
  const index = (sorted.length - 1) * q;
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  if (lower === upper) return sorted[lower];
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (index - lower);
}

export function rankedQuantile(values, q) {
  const sorted = values.filter(Number.isFinite).sort((a, b) => a - b);
  if (!sorted.length) return null;
  const index = Math.min(sorted.length - 1, Math.max(0, Math.floor((sorted.length - 1) * q)));
  return sorted[index];
}

export function clampScore(score) {
  if (!Number.isFinite(score)) return null;
  return Math.max(0, Math.min(100, Math.round(score)));
}

// Clean-confidence score: a detector that found nothing to flag starts at 100
// and erodes toward a 90 floor as coverage thins or the engine ran near a
// trigger. The 90 floor keeps a clean result above the 88 review band, so a
// low-confidence clean never reads as a finding.
export function cleanConfidenceScore(penalty) {
  const docked = Number.isFinite(penalty) ? Math.max(0, penalty) : 0;
  return Math.max(90, Math.min(100, Math.round(100 - docked)));
}

// Linear shortfall penalty: 0 at or above `full`, `maxPenalty` at or below
// `min`, interpolating between. Docks clean confidence when a detector had only
// just enough data to judge.
export function shortfallPenalty(value, full, min, maxPenalty) {
  if (!Number.isFinite(value) || value >= full) return 0;
  if (value <= min) return maxPenalty;
  return maxPenalty * ((full - value) / (full - min));
}

export function median(values) {
  const sorted = values.filter(Number.isFinite).sort((a, b) => a - b);
  if (!sorted.length) return null;
  return sorted[Math.floor(sorted.length / 2)];
}

export function finiteValues(points, key) {
  return points.map((point) => point[key]).filter(Number.isFinite);
}

export function elapsedAnchoredSampleStartOffset(data, step) {
  const firstElapsed = data.find((point) => Number.isFinite(point?.elapsedSeconds))?.elapsedSeconds;
  const roundedElapsed = Math.round(firstElapsed);
  const hasIntegerElapsed = Number.isFinite(firstElapsed) && Math.abs(firstElapsed - roundedElapsed) < 1e-6;
  return hasIntegerElapsed
    ? (step - (((roundedElapsed % step) + step) % step)) % step
    : 0;
}

export function elapsedAnchoredSampleForAnalysis(data, targetCount, { includeLast = true } = {}) {
  if (!Array.isArray(data)) return [];
  if (!Number.isFinite(targetCount) || targetCount <= 0 || data.length <= targetCount) return data;
  const step = Math.ceil(data.length / targetCount);
  const sampled = [];
  // Anchor decimation to elapsed time, so trimming a leading endpoint sample
  // does not flip the odd/even sample phase for short candidate windows.
  const startOffset = elapsedAnchoredSampleStartOffset(data, step);
  for (let index = startOffset; index < data.length; index += step) sampled.push(data[index]);
  const last = data.at(-1);
  if (includeLast && last && sampled.at(-1) !== last) sampled.push(last);
  return sampled;
}

export function groundContactState(point) {
  if (typeof point?.onGround === "boolean") return point.onGround;
  if (typeof point?.weightOnWheels === "boolean") return point.weightOnWheels;
  return null;
}

export function groundContactStats(points = []) {
  let known = 0;
  let ground = 0;
  let airborne = 0;
  points.forEach((point) => {
    const state = groundContactState(point);
    if (typeof state !== "boolean") return;
    known += 1;
    if (state) ground += 1;
    else airborne += 1;
  });
  return {
    known,
    ground,
    airborne,
    coverage: points.length ? known / points.length : 0,
    groundShare: known ? ground / known : null,
    airborneShare: known ? airborne / known : null,
  };
}

export function mostlyGroundContact(points = [], minShare = 0.8) {
  const stats = groundContactStats(points);
  if (!stats.known) return null;
  return (stats.groundShare ?? 0) >= minShare;
}

export function mostlyAirborneContact(points = [], minShare = 0.8) {
  const stats = groundContactStats(points);
  if (!stats.known) return null;
  return (stats.airborneShare ?? 0) >= minShare;
}

export function peerAverage(point, keys, excludedKey, excludedKeys = new Set()) {
  let total = 0;
  let count = 0;
  for (const key of keys) {
    if (key === excludedKey || excludedKeys.has(key)) continue;
    const value = point?.[key];
    if (!Number.isFinite(value)) continue;
    total += value;
    count += 1;
  }
  return count ? total / count : null;
}

export function pointCylinderValues(point, keys = cylinderKeys("egt")) {
  const values = [];
  for (const key of keys) {
    const value = point?.[key];
    if (Number.isFinite(value)) values.push(value);
  }
  return values;
}

export function thermalEngineWorkPoint(point) {
  const phase = String(point.phase ?? "");
  if (["Startup", "Shutdown"].includes(phase)) return false;
  const egtAverage = averageValues(pointCylinderValues(point, cylinderKeys("egt")));
  const chtAverage = averageValues(pointCylinderValues(point, cylinderKeys("cht")));
  return (Number.isFinite(egtAverage) && egtAverage > 850)
    || (Number.isFinite(chtAverage) && chtAverage > 240)
    || (Number.isFinite(point.fuelFlow) && point.fuelFlow > 4)
    || (Number.isFinite(point.rpm) && point.rpm > 1500)
    || (Number.isFinite(point.map) && point.map > 16);
}

function firstFiniteForKeys(point, keys) {
  for (const key of keys) {
    if (Number.isFinite(point?.[key])) return point[key];
  }
  return null;
}

export function inferredEngineStartTime(points) {
  const runningCue = (points ?? []).find((point) => {
    const rpm = firstFiniteForKeys(point, ["rpm", "rpm1", "rpm2"]);
    const fuelFlow = firstFiniteForKeys(point, ["fuelFlow", "fuelFlow1", "fuelFlow2"]);
    return (Number.isFinite(rpm) && rpm >= 500)
      || (Number.isFinite(fuelFlow) && fuelFlow >= 0.8);
  });
  return Number.isFinite(runningCue?.elapsedSeconds) ? runningCue.elapsedSeconds : null;
}

export function engineRunningTrendRows(points) {
  const rows = Array.isArray(points) ? points : [];
  const startTime = inferredEngineStartTime(rows);
  const filtered = rows.filter((point) => {
    const phase = String(point?.phase ?? "");
    if (phase === "Startup" || phase === "Shutdown") return false;
    if (!Number.isFinite(startTime)) return true;
    const elapsed = point?.elapsedSeconds;
    return !Number.isFinite(elapsed) || elapsed >= startTime;
  });
  if (filtered.length) return filtered;
  return rows.filter((point) => {
    const phase = String(point?.phase ?? "");
    return phase !== "Startup" && phase !== "Shutdown";
  });
}

export function lowPowerEngineWorkPoint(point) {
  const rpm = point.rpm;
  const fuelFlow = point.fuelFlow;
  return (Number.isFinite(rpm) && rpm > 450) || (Number.isFinite(fuelFlow) && fuelFlow > 1.5);
}

export function rollingMedianStep(data) {
  const steps = [];
  for (let index = 1; index < data.length; index += 1) {
    const step = (data[index].elapsedSeconds ?? index) - (data[index - 1].elapsedSeconds ?? index - 1);
    if (Number.isFinite(step) && step > 0) steps.push(step);
  }
  return median(steps) ?? 1;
}

type EngineMetricEntry = {
  engineKey: string;
  label: string;
  shortLabel?: string;
  result: Record<string, any>;
};

// Keep source keys and cylinder numbers intact; translate only display text.
export function labelEngineMetricResult(value, descriptors, field = "") {
  if (typeof value === "string") {
    return /^(?:note|label|summary|summaryMention|detail|description|interpretationHint|interpretationHints|reason|reasons|confidenceReasons|inconclusiveReasons|hints|headline)$/.test(field)
      ? engineScopedText(value, descriptors) : value;
  }
  if (Array.isArray(value)) return value.map((item) => labelEngineMetricResult(item, descriptors, field));
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, labelEngineMetricResult(item, descriptors, key)]));
}

// Retain each engine's result, including missing-data limits, in the rollup.
export function combineEngineMetricResults(entries: EngineMetricEntry[]) {
  const scored = entries.filter((entry) => Number.isFinite(entry.result.score));
  const worst = scored.reduce((selected, entry) => !selected || entry.result.score < selected.result.score ? entry : selected, null as EngineMetricEntry | null);
  const result = (worst ?? entries[0]).result;
  return {
    ...result,
    score: worst?.result.score ?? null,
    note: entries.map((entry) => `${entry.label}: ${entry.result.note}`).join("; "),
    details: {
      ...result.details,
      engines: entries.map((entry) => ({
        engineKey: entry.engineKey,
        label: entry.label,
        shortLabel: entry.shortLabel,
        score: entry.result.score,
        note: entry.result.note,
        details: entry.result.details,
      })),
      worstEngineKey: worst?.engineKey ?? null,
      engineId: worst?.engineKey ?? null,
      engineLabel: worst?.label ?? null,
      coverage: scored.length === entries.length ? "complete" : scored.length ? "partial" : "unavailable",
    },
  };
}

export function analyzePerEngineContext(data, descriptors, analyze) {
  if (!Array.isArray(descriptors) || descriptors.length < 2) return analyze(data, descriptors?.[0]);
  const entries = descriptors.map((engine) => {
    const result = labelEngineMetricResult(analyze(projectFlightDataForEngine(data, engine), engine), descriptors);
    const identity = { engineId: engine.id, engineKey: engine.engineKey, engineLabel: engine.label, engineShortLabel: engine.shortLabel };
    const details = { ...result.details, ...identity };
    for (const field of ["events", "windows"]) {
      if (!Array.isArray(details[field])) continue;
      details[field] = details[field].map((event) => ({
        ...event,
        ...identity,
        ...(event.id ? { id: `${engine.id}:${event.id}` } : {}),
        ...(event.key ? { key: `${engine.id}:${event.key}` } : {}),
        ...(event.label ? { label: `${engine.label}: ${event.label}` } : {}),
      }));
    }
    return { engineKey: engine.engineKey ?? engine.id, label: engine.label, shortLabel: engine.shortLabel, result: { ...result, details } };
  });
  const combined = combineEngineMetricResults(entries);
  for (const field of ["events", "windows"]) {
    if (!entries.some((entry) => Array.isArray(entry.result.details[field]))) continue;
    combined.details[field] = entries.flatMap((entry) => entry.result.details[field] ?? []).sort((a, b) => a.startTime - b.startTime);
    combined.details[field === "events" ? "eventCount" : "windowCount"] = combined.details[field].length;
  }
  combined.details.summaryMention = entries.map(({ label, result }) => `${label}: ${result.details.summaryMention ?? result.note}`).join(" ");
  return combined;
}
